import React, { useState } from 'react';
import { Languages, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { autoTranslate, LANGUAGES } from '../services/translationService';

const TranslateButton = ({ text, onTranslate, className = '' }) => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [translation, setTranslation] = useState(null);
  const [error, setError] = useState(null);

  const handleTranslate = async () => {
    if (translation) {
      // Toggle off - clear translation
      setTranslation(null);
      if (onTranslate) onTranslate(null);
      return;
    }

    setIsTranslating(true);
    setError(null);

    try {
      const result = await autoTranslate(text);
      
      if (result.success) {
        setTranslation({
          text: result.translatedText,
          fromLang: result.fromLang,
          toLang: result.toLang
        });
        if (onTranslate) onTranslate(result.translatedText);
      } else {
        setError(result.error || 'Translation failed');
      }
    } catch (err) {
      setError('Translation failed. Please try again.');
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <div className={`inline-flex items-center ${className}`}>
      <button
        onClick={handleTranslate}
        disabled={isTranslating}
        className={`flex items-center gap-1 text-xs transition-colors ${
          translation 
            ? 'text-blue-500 hover:text-blue-600' 
            : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
        }`}
        title={translation ? 'Hide translation' : 'Translate message'}
        data-testid="translate-btn"
      >
        {isTranslating ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : translation ? (
          <X className="h-3 w-3" />
        ) : (
          <Languages className="h-3 w-3" />
        )}
        <span className="hidden sm:inline">
          {isTranslating ? 'Translating...' : translation ? 'Hide' : 'Translate'}
        </span>
      </button>
      
      {error && (
        <span className="ml-2 text-xs text-red-500">{error}</span>
      )}
    </div>
  );
};

// Message bubble with translation support
export const TranslatableMessage = ({ 
  message, 
  isOwnMessage, 
  showTimestamp = true,
  currentUserId 
}) => {
  const [translation, setTranslation] = useState(null);

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[70%] rounded-2xl px-4 py-2 ${
          isOwnMessage
            ? 'bg-blue-600 text-white rounded-br-md'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-md'
        }`}
      >
        {/* Original message */}
        <p className="break-words">{message.content}</p>
        
        {/* Translation */}
        {translation && (
          <div className={`mt-2 pt-2 border-t ${
            isOwnMessage ? 'border-blue-500/30' : 'border-gray-300 dark:border-gray-600'
          }`}>
            <p className={`text-xs mb-1 ${isOwnMessage ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'}`}>
              Translation:
            </p>
            <p className={`break-words ${isOwnMessage ? 'text-blue-100' : 'text-gray-700 dark:text-gray-300'}`}>
              {translation}
            </p>
          </div>
        )}
        
        {/* Footer with time and translate button */}
        <div className={`flex items-center justify-between mt-1 ${
          isOwnMessage ? 'text-blue-200' : 'text-gray-400 dark:text-gray-500'
        }`}>
          <TranslateButton 
            text={message.content} 
            onTranslate={setTranslation}
            className={isOwnMessage ? 'text-blue-200' : ''}
          />
          {showTimestamp && (
            <span className="text-xs ml-2">
              {new Date(message.created_at).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranslateButton;
